<template>
    <widgetModal title="Change Password" subtitle="Update the user's password with secure requirements"
        :btnlefttext="isSubmitting ? 'Changing...' : 'Change Password'" btncentertext="Cancel"
        :leftloading="isSubmitting" :rightloading="false" :centerloading="false" :footerhidden="false"
        :hiderightbtn="true" footer_padding="10px 18px 30px 30px" header_padding="30px 0px 0px 31px"
        body_padding="0px 30px" width="500px" @onLeftClick="submitPasswordChange()" @onCenterClick="onCancel()">

        <div class="change-password-content">
            <!-- User Info Header -->
            <div class="user-info-header">
                <widgetProfile :title="user?.first_name + ' ' + user?.last_name"
                    :subtitle="user?.profile ? user?.profile.address : ''" :file_name="user?.profile_picture"
                    :path="$fullUrl('images/profiles/')"></widgetProfile>
            </div>

            <!-- Password Form -->
            <div class="password-form">
                <widgetInputWrapper title="New Password">
                    <widgetInput v-model="passwordForm.newPassword" type="password" placeholder="Enter new password"
                        :class="{ error: passwordErrors.newPassword }" />
                    <span v-if="passwordErrors.newPassword" class="error-message">{{ passwordErrors.newPassword
                        }}</span>
                </widgetInputWrapper>

                <widgetInputWrapper title="Confirm Password">
                    <widgetInput v-model="passwordForm.confirmPassword" type="password"
                        placeholder="Confirm new password" :class="{ error: passwordErrors.confirmPassword }" />
                    <span v-if="passwordErrors.confirmPassword" class="error-message">{{ passwordErrors.confirmPassword
                        }}</span>
                </widgetInputWrapper>

                <!-- Password Requirements -->
                <div class="password-requirements">
                    <h4>Password Requirements:</h4>
                    <ul>
                        <li :class="{ valid: passwordValidation.length }">
                            <span class="requirement-icon">{{ passwordValidation.length ? '✓' : '✗' }}</span>
                            At least 8 characters long
                        </li>
                        <li :class="{ valid: passwordValidation.uppercase }">
                            <span class="requirement-icon">{{ passwordValidation.uppercase ? '✓' : '✗' }}</span>
                            One uppercase letter
                        </li>
                        <li :class="{ valid: passwordValidation.lowercase }">
                            <span class="requirement-icon">{{ passwordValidation.lowercase ? '✓' : '✗' }}</span>
                            One lowercase letter
                        </li>
                        <li :class="{ valid: passwordValidation.number }">
                            <span class="requirement-icon">{{ passwordValidation.number ? '✓' : '✗' }}</span>
                            One number
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </widgetModal>
</template>

<script>
export default {
    name: 'ChangePasswordModal',
    props: {
        user: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            isSubmitting: false,
            passwordForm: {
                newPassword: "",
                confirmPassword: ""
            },
            passwordErrors: {
                newPassword: "",
                confirmPassword: ""
            }
        };
    },
    computed: {
        passwordValidation() {
            const password = this.passwordForm.newPassword;
            return {
                length: password.length >= 8,
                uppercase: /[A-Z]/.test(password),
                lowercase: /[a-z]/.test(password),
                number: /\d/.test(password)
            };
        },

        isPasswordValid() {
            const validation = this.passwordValidation;
            const passwordsMatch = this.passwordForm.newPassword === this.passwordForm.confirmPassword;
            const allRequirementsMet = validation.length && validation.uppercase && validation.lowercase && validation.number;

            return allRequirementsMet && passwordsMatch && this.passwordForm.newPassword.length > 0;
        }
    },
    watch: {
        user(newUser) {
            if (newUser) {
                this.resetForm();
            }
        }
    },
    methods: {
        resetForm() {
            this.passwordForm = {
                newPassword: "",
                confirmPassword: ""
            };
            this.passwordErrors = {
                newPassword: "",
                confirmPassword: ""
            };
            this.isSubmitting = false;
        },

        validatePassword() {
            this.passwordErrors = {
                newPassword: "",
                confirmPassword: ""
            };

            // Validate new password
            if (this.passwordForm.newPassword.length < 8) {
                this.passwordErrors.newPassword = "Password must be at least 8 characters long";
            } else if (!/[A-Z]/.test(this.passwordForm.newPassword)) {
                this.passwordErrors.newPassword = "Password must contain at least one uppercase letter";
            } else if (!/[a-z]/.test(this.passwordForm.newPassword)) {
                this.passwordErrors.newPassword = "Password must contain at least one lowercase letter";
            } else if (!/\d/.test(this.passwordForm.newPassword)) {
                this.passwordErrors.newPassword = "Password must contain at least one number";
            }

            // Validate password confirmation
            if (this.passwordForm.confirmPassword !== this.passwordForm.newPassword) {
                this.passwordErrors.confirmPassword = "Passwords do not match";
            }

            return !this.passwordErrors.newPassword && !this.passwordErrors.confirmPassword;
        },

        async submitPasswordChange() {
            if (!this.validatePassword()) {
                return;
            }

            this.isSubmitting = true;

            try {
                const payload = {
                    user_id: this.user.id,
                    new_password: this.passwordForm.newPassword,
                    confirm_password: this.passwordForm.confirmPassword
                };

                const response = await this.$store.dispatch('USER_CHANGE_PASSWORD', payload);

                if (response.data && response.data.message == "success") {
                    // Emit success event to parent
                    this.$emit('onPasswordChanged', {
                        user: this.user,
                        newPassword: this.passwordForm.newPassword
                    });

                    this.onCancel();
                } else {
                    this.$emit('onError', new Error(response.data?.message || 'Failed to change password'));
                }
            } catch (error) {
                console.error("Error changing password:", error);
                this.$emit('onError', error);
            } finally {
                this.isSubmitting = false;
            }
        },

        onCancel() {
            this.resetForm();
            this.$emit('onCancel');
        }
    }
};
</script>

<style lang="scss" scoped>
.change-password-content {
    .user-info-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 8px;
        margin-bottom: 24px;

        .user-avatar {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            object-fit: cover;
        }

        .user-details {
            .user-name {
                font-weight: 600;
                color: #2c3e50;
                font-size: 16px;
                margin-bottom: 4px;
            }

            .user-email {
                color: #6c757d;
                font-size: 14px;
            }
        }
    }

    .password-form {
        .error-message {
            display: block;
            color: #dc3545;
            font-size: 12px;
            margin-top: 4px;
        }

        .password-requirements {
            background: #f8f9fa;
            padding: 16px;
            border-radius: 6px;
            margin-top: 20px;

            h4 {
                margin: 0 0 12px;
                font-size: 14px;
                color: #495057;
                font-weight: 600;
            }

            ul {
                margin: 0;
                padding: 0;
                list-style: none;

                li {
                    padding: 4px 0;
                    font-size: 13px;
                    color: #6c757d;
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .requirement-icon {
                        font-weight: bold;
                        color: #dc3545;
                        width: 16px;
                        text-align: center;
                    }

                    &.valid {
                        color: #28a745;

                        .requirement-icon {
                            color: #28a745;
                        }
                    }
                }
            }
        }
    }
}

// Override input error styles
::v-deep .error {
    .widget_input {
        border-color: #dc3545 !important;

        &:focus {
            box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.1) !important;
        }
    }
}
</style>